import { type Breakpoint, Box, useMediaQuery, useTheme } from '@mui/material';
import { createContext, type ReactNode, useContext } from 'react';

const RoomyActionsContext = createContext(false);

/**
 * A row that action buttons have to themselves — the header actions that drop
 * under a tab strip on a phone, for one. They keep their labels here however
 * narrow the screen is, and share the row's full width between them.
 * @param props - Component props
 * @param props.children - The actions
 * @returns The row of actions
 */
export function RoomyActions({ children }: { children: ReactNode }) {
  return (
    <RoomyActionsContext.Provider value>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          // Pages hand their actions over either bare or already in a row of
          // their own; stretch whichever it is.
          '& > *': { flex: '1 1 auto' },
          '& > .MuiStack-root': { minHeight: 0 },
          '& > .MuiStack-root > *': { flex: '1 1 auto' },
        }}
      >
        {children}
      </Box>
    </RoomyActionsContext.Provider>
  );
}

/**
 * Whether a header action should drop its label and draw as an icon: below
 * `collapseBelow`, unless it sits inside {@link RoomyActions}.
 * @param collapseBelow - The breakpoint under which the label goes
 * @returns True when the button should be icon-only
 */
export function useCollapseToIcon(collapseBelow: Breakpoint = 'md'): boolean {
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down(collapseBelow));
  const roomy = useContext(RoomyActionsContext);
  return narrow && !roomy;
}
